import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { BedDouble, ArrowRight, MapPin } from "lucide-react";

const CATEGORY_LABELS = {
  apartment: "Appartement", villa: "Villa", house: "Maison",
  studio: "Studio", hotel_room: "Chambre",
};

/**
 * Compact lodging strip displayed on agency-related surfaces
 * (search grouped results, vehicle detail sidebar).
 * MOBIKAR reste centré véhicules — les logements ne s'affichent QUE
 * quand une agence en propose. Aucun logement → composant invisible.
 */
export default function AgencyLodgingsStrip({ agencyId, title = "Logements aussi proposés par cette agence", limit = 3, compact = false }) {
  const { fmt } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agencyId) { setLoading(false); return; }
    api.get(`/lodgings?agency_id=${agencyId}`)
      .then((r) => setItems((r.data || []).slice(0, limit)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [agencyId, limit]);

  if (loading || items.length === 0) return null;

  return (
    <div className={`mt-6 ${compact ? "" : "border-t border-gray-100 pt-6"}`} data-testid={`agency-lodgings-${agencyId}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BedDouble className="h-4 w-4 text-[#0e7a3f]" />
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">{title}</div>
        </div>
        <span className="text-[11px] font-bold text-[#0e7a3f]">Service complémentaire</span>
      </div>
      <div className={`grid gap-4 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"}`}>
        {items.map((l) => (
          <Link
            key={l.id}
            to={`/lodging/${l.id}`}
            data-testid={`agency-lodging-${l.id}`}
            className="group flex gap-3 bg-white rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-0.5 hover:shadow-md transition-all"
          >
            <div className="w-24 h-24 flex-shrink-0 bg-gray-100 overflow-hidden">
              {l.photos?.[0]
                ? <img src={l.photos[0]} alt={l.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                : <div className="w-full h-full flex items-center justify-center text-gray-300"><BedDouble className="h-6 w-6" /></div>}
            </div>
            <div className="flex-1 min-w-0 py-2 pr-3">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0e7a3f]/10 text-[#0e7a3f]">
                {CATEGORY_LABELS[l.category] || l.category}
              </span>
              <div className="font-semibold text-sm text-[#0a1e3f] truncate mt-1">{l.name}</div>
              <div className="text-[11px] text-gray-500 flex items-center gap-1 truncate"><MapPin className="h-3 w-3" /> {l.city}</div>
              <div className="mt-1 flex items-center justify-between">
                <div className="font-display font-black text-sm text-[#0a1e3f]">{fmt(l.price_per_night)}<span className="text-[10px] font-normal text-gray-400"> /nuit</span></div>
                <ArrowRight className="h-3 w-3 text-[#0e7a3f]" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
